import React from 'react';
import { cleanProceedingName } from '../../lib/cleanProceedingName';
import { ArrowLeft, Loader, ExternalLink } from 'lucide-react';
import { BookmarkButton } from '../BookmarkButton';
import { FollowButton } from '../FollowButton';
import type { DeadlineExtension } from '../../hooks/useDocketMetadata';

export type ProceedingTabId =
  | 'overview'
  | 'nprm_questions'
  | 'public_notice_questions'
  | 'filings'
  | 'comment_summaries'
  | 'chat';

export type PrevailingInstrument = 'nprm' | 'public_notice' | 'neither';

export interface ProceedingHeaderTab {
  id: ProceedingTabId;
  label: string;
  icon: React.ReactNode;
  count?: string;
}

interface ProceedingDetailHeaderProps {
  docketNumber: string;
  proceedingName: string;
  status: string;
  lastProcessedAt: string | null;
  onBack: () => void;
  activeTab: ProceedingTabId;
  onTabChange: (tab: ProceedingTabId) => void;
  tabs: ProceedingHeaderTab[];
  fileCount: number | null;
  publicCommentCount: number | null;
  nprmCount: number | null;
  publicNoticeCount: number | null;
  prevailingInstrument: PrevailingInstrument;
  fetchingFilingCount: boolean;
  fetchingNprmCount: boolean;
  commentDeadline: Date | null;
  replyCommentDeadline: Date | null;
  commentDeadlineExtension?: DeadlineExtension | null;
  replyDeadlineExtension?: DeadlineExtension | null;
  fetchingDeadlines: boolean;
  /** Local / wall-clock dates (e.g. last processed). */
  formatShortDate: (iso: string) => string;
  /** FCC regulatory deadlines — UTC calendar day to match NPRM timeline. */
  formatRegulatoryShortDate: (iso: string) => string;
}

export const ProceedingDetailHeader: React.FC<ProceedingDetailHeaderProps> = ({
  docketNumber,
  proceedingName,
  status,
  lastProcessedAt,
  onBack,
  activeTab,
  onTabChange,
  tabs,
  fileCount,
  publicCommentCount,
  nprmCount,
  publicNoticeCount,
  prevailingInstrument,
  fetchingFilingCount,
  fetchingNprmCount,
  commentDeadline,
  replyCommentDeadline,
  commentDeadlineExtension,
  replyDeadlineExtension,
  fetchingDeadlines,
  formatShortDate,
  formatRegulatoryShortDate,
}) => {
  // WAI-ARIA tabs: roving tabindex + arrow-key navigation. Only the selected
  // tab is in the tab order; Left/Right/Home/End move selection and focus.
  const tabRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const ids = tabs.map((t) => t.id);
    const idx = ids.indexOf(activeTab);
    let next = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % ids.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + ids.length) % ids.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ids.length - 1;
    else return;
    e.preventDefault();
    const nextId = ids[next];
    onTabChange(nextId);
    tabRefs.current[nextId]?.focus();
  };

  const statVal = (node: React.ReactNode) => (
    <span className="text-lg font-medium tracking-tight text-white sm:text-xl">{node}</span>
  );
  const statLabel = (text: string) => (
    <span className="text-[11px] font-light text-white/40">{text}</span>
  );

  const filingsDisplay = fetchingFilingCount ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : fileCount !== null ? (
    fileCount.toLocaleString()
  ) : (
    '—'
  );

  const publicCommentsDisplay = fetchingFilingCount ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : publicCommentCount !== null ? (
    publicCommentCount.toLocaleString()
  ) : (
    '—'
  );

  const nprmDisplay = fetchingNprmCount ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : prevailingInstrument === 'public_notice' ? (
    '—'
  ) : nprmCount !== null ? (
    nprmCount.toLocaleString()
  ) : (
    '—'
  );

  const publicNoticeDisplay = fetchingNprmCount ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : prevailingInstrument === 'nprm' ? (
    '—'
  ) : publicNoticeCount !== null ? (
    publicNoticeCount.toLocaleString()
  ) : (
    '—'
  );

  const commentPeriodDisplay = fetchingDeadlines ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : commentDeadline ? (
    formatRegulatoryShortDate(commentDeadline.toISOString())
  ) : (
    '—'
  );

  const replyDisplay = fetchingDeadlines ? (
    <Loader className="h-5 w-5 animate-spin text-white/60" />
  ) : replyCommentDeadline ? (
    formatRegulatoryShortDate(replyCommentDeadline.toISOString())
  ) : (
    '—'
  );

  const lastProcessedDisplay = lastProcessedAt ? formatShortDate(lastProcessedAt) : '—';

  /**
   * Surface a deadline extension under its stat: "↳ extended from May 26
   * (DA-26-510A1.pdf)" with a clickable link to the FCC order PDF. Hidden
   * when no extension event is recorded for the period.
   */
  const renderExtensionFooter = (ext: DeadlineExtension | null | undefined) => {
    if (!ext || !ext.from_date) return null;
    // Format the prior date in the same UTC-day convention the timeline uses.
    let fromLabel = ext.from_date;
    try {
      fromLabel = formatRegulatoryShortDate(new Date(`${ext.from_date}T00:00:00Z`).toISOString());
    } catch {
      // fall through with the raw YYYY-MM-DD string
    }
    // The order PDFs live on docs.fcc.gov under the bare filename — same
    // pattern as the source_url stored in document_paths for the order
    // filing on the docket.
    const orderUrl = ext.by_filename
      ? `https://docs.fcc.gov/public/attachments/${ext.by_filename}`
      : null;
    const orderLabel = ext.by_filename?.replace(/\.pdf$/i, '') || 'Order';
    return (
      <div className="mt-0.5 text-[10px] font-light text-white/55">
        <span className="mr-1">↳ extended from {fromLabel}</span>
        {orderUrl ? (
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-white/65 underline decoration-white/25 underline-offset-2 hover:text-white hover:decoration-white/60"
            title={ext.by_filer ? `Order ${orderLabel} — filed by ${ext.by_filer}` : `Order ${orderLabel}`}
          >
            ({orderLabel})
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          <span>({orderLabel})</span>
        )}
      </div>
    );
  };

  const stats: { label: string; value: React.ReactNode; footer?: React.ReactNode }[] = [
    { label: 'Filings', value: filingsDisplay },
    ...(publicCommentCount && publicCommentCount > 0
      ? [{ label: 'Public comments', value: publicCommentsDisplay }]
      : []),
    {
      label: 'Comments due',
      value: commentPeriodDisplay,
      footer: renderExtensionFooter(commentDeadlineExtension),
    },
    {
      label: 'Reply due',
      value: replyDisplay,
      footer: renderExtensionFooter(replyDeadlineExtension),
    },
    { label: nprmCount === 1 ? 'NPRM' : 'NPRMs', value: nprmDisplay },
    { label: publicNoticeCount === 1 ? 'Public Notice' : 'Public Notices', value: publicNoticeDisplay },
    { label: 'Last processed', value: lastProcessedDisplay },
  ];

  return (
    <header
      className="w-full border-b border-white/[0.08] bg-[var(--cg-nav)]"
      style={{ backgroundColor: 'var(--cg-nav)' }}
    >
      <div className="px-4 pb-0 pt-5 sm:px-9">
        <button
          type="button"
          onClick={onBack}
          className="mb-4 flex items-center gap-1.5 text-xs text-white/35 transition-colors hover:text-white/70"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Proceedings
        </button>

        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-3">
              <h1 className="font-mono text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {docketNumber}
              </h1>
              {status === 'open' && (
                <span
                  className="rounded-full border px-2.5 py-0.5 text-[11px] font-medium tracking-wide"
                  style={{
                    color: 'color-mix(in srgb, var(--cg-green) 85%, white)',
                    backgroundColor: 'color-mix(in srgb, var(--cg-green) 22%, transparent)',
                    borderColor: 'color-mix(in srgb, var(--cg-green) 35%, transparent)',
                  }}
                >
                  Open
                </span>
              )}
            </div>
            <p className="max-w-3xl text-sm font-light text-white/50 sm:text-[15px]">
              {cleanProceedingName(proceedingName) || 'No description available'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <FollowButton
              docketNumber={docketNumber}
              tone="dark"
              className="hover:scale-100"
            />
            <BookmarkButton
              docketNumber={docketNumber}
              title={proceedingName || docketNumber}
              tone="dark"
              className="hover:scale-100"
            />
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-y-4">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`flex min-w-[4.5rem] flex-col gap-0.5 pb-1 ${
                i > 0 ? 'border-l border-white/10 pl-5 sm:pl-8' : 'pr-2'
              }`}
            >
              {statVal(s.value)}
              {statLabel(s.label)}
              {s.footer}
            </div>
          ))}
        </div>

        <div
          // Focus lives on the tabs via roving tabindex (WAI tabs pattern),
          // so the tablist itself isn't a focus target.
          role="tablist"
          aria-label="Docket sections"
          onKeyDown={handleTabKeyDown}
          className="-mb-px flex gap-0 overflow-x-auto border-t border-white/[0.08] scrollbar-thin"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`proceeding-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls="proceeding-tabpanel"
                tabIndex={isActive ? 0 : -1}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                onClick={() => onTabChange(tab.id)}
                className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-[13px] transition-colors sm:px-4 ${
                  isActive
                    ? 'border-[var(--cg-green)] text-white'
                    : 'border-transparent text-white/40 hover:text-white/75'
                }`}
              >
                <span className={isActive ? 'text-white' : 'text-white/50'} aria-hidden="true">{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] ${
                      isActive
                        ? 'bg-emerald-500/25 text-emerald-100'
                        : 'bg-white/10 text-white/45'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};
